import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Grid, Row, Col } from '@zendeskgarden/react-grid';
import Flex from 'styled-flex-component';
import { Button } from 'theme/Button';
import { get, find, map } from 'lodash';
import { variables } from 'theme/variables';
import Spinner from 'components/Spinner/Spinner';
import Icon from 'components/Icon/Icon';
import SVGLogo from 'components/AppHeader/components/SVGLogo';
import { contentStatusMap } from 'utility/staticMap.ts';
import { Dropdown, Trigger, Menu, Item } from '@zendeskgarden/react-dropdowns';
import { PropTypes } from 'prop-types';
import Metadata from './Metadata';
import { MetadataWrapper } from './MegadraftWrapper.styles';
import { pluginPropsShape } from '../../propTypesObjects';

const { custom_blue: customBlue } = variables;

const style = {
  height: '30px',
  lineHeight: '30px',
  padding: '0 20px',
  minWidth: '20px',
};

function EditorHeader({
  contentId,
  canRenderMeta,
  metadata,
  saving,
  readOnly,
  onChangeStatus,
  status,
  toggleReadOnly,
  clientId,
  contentType,
  tags,
  category,
  saveContent,
  close,
  pluginProps,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [editor, setEditor] = useState(false);
  const [lastSaved, setLastSavedTime] = useState(false);
  const [initial] = useState(!contentId);
  useEffect(() => {
    if (canRenderMeta && !metadata) {
      setEditor(true);
    }
  }, [canRenderMeta, metadata]);

  useEffect(() => {
    if (saving && !isSaving) {
      setIsSaving(true);
    } else if (!saving && isSaving) {
      setIsSaving(true);
      setLastSavedTime(moment().format());
    }
  }, [isSaving, saving]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: '#fff',
        zIndex: 2,
      }}
    >
      <div
        style={{
          maxWidth: readOnly ? '970px' : '1000px',
          margin: '0 auto',
          padding: '20px 0',
        }}
      >
        <Grid>
          <Row>
            <Col style={{ maxWidth: '200px', width: '100%' }}>
              <SVGLogo />
            </Col>
            <Col alignCenter justifyEnd>
              <Row>
                <Col size="auto" alignCenter>
                  <div
                    style={
                      saving || lastSaved ? { opacity: 1 } : { opacity: 0 }
                    }
                  >
                    <Flex
                      alignCenter
                      style={
                        saving
                          ? { width: '30px', height: '30px' }
                          : { height: '30px' }
                      }
                    >
                      {saving && <Spinner />}
                      {lastSaved && !saving ? (
                        <span style={{ fontSize: '11px' }}>
                          Saved {moment(lastSaved).fromNow()}
                        </span>
                      ) : null}
                    </Flex>
                  </div>
                </Col>
                <Col>
                  <StatusMenuDropdown
                    onChangeStatus={onChangeStatus}
                    status={status}
                  />
                </Col>
                <Col>
                  <Button
                    primaryOutline
                    size="small"
                    style={style}
                    onClick={toggleReadOnly}
                  >
                    {readOnly ? 'Edit' : 'Preview'}
                  </Button>
                </Col>
                {contentId ? (
                  <Col>
                    <Button
                      onClick={() => window.open(`/blog/preview/${contentId}`)}
                      link
                      blue
                      style={{
                        height: '30px',
                        lineHeight: '30px',
                        padding: '0px 8px',
                        minWidth: '80px',
                        fontSize: '13px',
                      }}
                    >
                      Go To Post
                    </Button>
                  </Col>
                ) : null}
                <Col style={{ paddingRight: 0 }}>
                  <Icon
                    onClick={() => setEditor(!editor)}
                    buttonStyle
                    style={{ height: '30px' }}
                    icon="icon-settings"
                    fontSize="16px"
                    color={editor ? customBlue : '#999'}
                  />
                  <MetadataWrapper initial={initial} open={editor}>
                    {editor ? (
                      <Metadata
                        editor={editor}
                        clientId={clientId}
                        contentType={contentType}
                        objectId={contentId}
                        setEditor={setEditor}
                        metadata={metadata}
                        tags={tags}
                        category={category}
                        saveContent={saveContent}
                        pluginProps={pluginProps}
                      />
                    ) : null}
                  </MetadataWrapper>
                </Col>
                <Col>
                  <Icon
                    onClick={close}
                    buttonStyle
                    style={{ height: '30px' }}
                    icon="icon-close"
                    fontSize="13px"
                    color="#999"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Grid>
      </div>
    </div>
  );
}

EditorHeader.propTypes = {
  contentId: PropTypes.number,
  canRenderMeta: PropTypes.bool,
  metadata: PropTypes.shape({}),
  saving: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChangeStatus: PropTypes.func,
  status: PropTypes.string,
  toggleReadOnly: PropTypes.func,
  clientId: PropTypes.number,
  contentType: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  category: PropTypes.number,
  saveContent: PropTypes.func,
  close: PropTypes.func,
  pluginProps: pluginPropsShape,
};

function StatusMenuDropdown({ status, onChangeStatus }) {
  const statusMap = get(find(contentStatusMap, { value: status }), 'label');
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  return (
    <Dropdown
      style={{ width: '200px' }}
      arrow={false}
      placement="bottom-end"
      onSelect={(item) => onChangeStatus(item)}
      downshiftProps={{ itemToString: (item) => item && item.label }}
      onStateChange={(changes) => {
        if (
          changes.isOpen !== menuIsOpen &&
          Object.prototype.hasOwnProperty.call(changes, 'isOpen')
        ) {
          setMenuIsOpen(changes.isOpen);
        }
      }}
    >
      <Trigger>
        <div>
          <Button primary size="small" style={style}>
            {statusMap ? (
              <span style={{ fontWeight: '600' }}>
                Status: <span>{statusMap}</span>
              </span>
            ) : (
              'Set Status'
            )}
          </Button>
        </div>
      </Trigger>
      {!menuIsOpen ? null : (
        <Menu placement="bottom" arrow>
          {map(contentStatusMap, (option) => (
            <Item
              hoverColorLight
              bold
              disabled={option.value === 'un_submitted'}
              key={option.value}
              value={option}
            >
              {option.label}
            </Item>
          ))}
        </Menu>
      )}
    </Dropdown>
  );
}

StatusMenuDropdown.propTypes = {
  status: PropTypes.string,
  onChangeStatus: PropTypes.func,
};

export default EditorHeader;
